import { DateTime } from 'luxon';
import humanize from 'humanize-duration';

import { Model } from '@app/models/model';
import { config } from '@config/config';
import { User } from '@app/models/users/users.model';
import { Location } from '@app/models/locations/locations.model';
import { Device } from '@app/models/devices/devices.model';
import { Query, Bucket } from '@app/models/queries/queries.model';

import { COLOR_CONSTANTS } from '../../shared/scss-variables.generated';

const COLORS = {
    RED: COLOR_CONSTANTS['$red-clay'],
    ORANGE: COLOR_CONSTANTS['$sunset'],
    GREEN: COLOR_CONSTANTS['$cut-grass'],
};

export class Notification extends Model {
    // Backend fields
    id: number;
    device_id: string;
    user_id: number;
    type: number;
    message: string;
    created_datetime: string;
    title: string;
    read: boolean;
    extra?: {
        query?: {
            request_id: string;
            since_datetime: string;
            until_datetime: string;
            tz: string;
            bucket: Bucket;
            group_multiplier: number;
            raw: boolean;
            device_id: string[];
        };
        percentage?: string | number;
        budget_start?: string;
        budget_type?: string;
        advanced_low_flow?: boolean;
        leak_id?: number;
        connected?: boolean;
        problem?: string;
    };
    event_triggered: boolean;
    event_rule?: string;

    // Frontend fields
    user: User;
    location: Location;
    device: Device;
    hexColor: string;
    typeString: string;
    createdString: string;
    usageQuery: Query;
    flowRate: string = 'N/A';
    duration: string = 'N/A';

    constructor(input: any) {
        super(input);
        this.formatColor();
        this.formatType();
        this.formatCreated();
        this.formatUsageQuery();
        this.formatDuration();
    }

    formatColor() {
        if (this.type === config.notificationTypes.USAGE_ALERT) {
            this.hexColor = this.title.indexOf('Leak') > -1 ? COLORS.RED : COLORS.ORANGE;
        } else if (this.type === config.notificationTypes.BUDGET) {
            this.hexColor =
                (this.extra.percentage as number) >= 100
                    ? COLORS.RED
                    : (this.extra.percentage as number) < 75
                    ? COLORS.GREEN
                    : COLORS.ORANGE;
        } else if (this.type === config.notificationTypes.HEARTBEAT) {
            if (this.extra && this.extra.connected) {
                this.hexColor = COLORS.GREEN;
            } else {
                this.hexColor = COLORS.RED;
            }
        } else if (
            this.type === config.notificationTypes.BATTERY ||
            this.type === config.notificationTypes.DEVICE_MOVED
        ) {
            this.hexColor = COLORS.RED;
        } else {
            this.hexColor = COLORS.GREEN;
        }
    }

    formatType() {
        if (this.type === config.notificationTypes.USAGE_ALERT) {
            this.typeString = this.event_rule ? `${this.event_rule} Usage Alert` : 'Usage Alert';
        } else {
            this.typeString = config.notificationTypeFormat[this.type];
        }
    }

    formatCreated() {
        this.createdString = DateTime.fromISO(this.created_datetime).toLocaleString(DateTime.DATETIME_MED);
    }

    formatUsageQuery() {
        if (this.extra && this.extra.query) {
            const q = this.extra.query;
            this.usageQuery = new Query({
                device_id: this.device_id,
                since_datetime: q.since_datetime,
                until_datetime: q.until_datetime,
                bucket: q.bucket,
                group_multiplier: 1,
                operation: 'AVG',
            });
        }
    }

    formatDuration() {
        if (this.type === config.notificationTypes.USAGE_ALERT && this.extra && this.extra.query) {
            this.duration = humanize(
                DateTime.fromSQL(this.extra.query.until_datetime).diff(
                    DateTime.fromSQL(this.extra.query.since_datetime),
                    'milliseconds'
                ).milliseconds,
                { largest: 1, round: true }
            );
        }
    }

    calculateFlowRate() {
        if (this.usageQuery && (this.usageQuery.value || this.usageQuery.value === 0)) {
            this.flowRate = `${this.usageQuery.value.toFixed(2)} gal / min`;
        }
    }

    calculateDuration() {
        if (this.type === config.notificationTypes.HEARTBEAT && this.device) {
            const dur = DateTime.fromISO(this.device.last_seen).diff(
                DateTime.fromISO(this.created_datetime),
                'milliseconds'
            ).milliseconds;
            if (dur > 600000) {
                this.duration = humanize(dur, { largest: 1, round: true });
            } else {
                this.duration = 'Unresolved';
            }
        }
    }
}
